import React from 'react';
import {LearnerListItem} from '../../../../data/models/Learners/Learner';
import './styles.css';
import {courseLearnerText} from '../courseLearnerText';
import Button from '../../../../uiToolkit/Buttons/Button';
import {
  ADD_LEARNERS_TAB,
  ENROLLED_LEARNERS_TAB,
} from '../container/CourseLearnerContainer';
import CourseNotPublished from '../../components/CourseNotPublished';
import DatePickerComponent from '../components/DatePickerComponent';
import ModalDialog from '../../../../modules/ModalDialog/ModalDialog';
import TextInput2 from '../../../../uiToolkit/Inputs/TextInput2/TextInput2';
import LoadingSpinner from '../../../../uiToolkit/LoadingSpinner/LoadingSpinner';
import LearnersTable from '../../../../modules/LearnersTable/LearnersTable';
import LearnersEmpty from '../../../../modules/LearnersTable/LearnersEmpty';
import LearnersSearch from '../../../../modules/LearnersTable/LearnersSearch';
import AudienceToggle, {
  Audience,
} from '../../../../modules/LearnerGroups/AudienceToggle';
import GroupsPicker from '../../../../modules/LearnerGroups/GroupsPicker';
import {LearnerGroup} from '../../../../data/models/Groups/LearnerGroup';

interface Props {
  learnerData: LearnerListItem[];
  onSave: (selectedLearners: string[], dueDate?: Date) => void;
  pages: number;
  currentPage: number;
  onPageChange: (page: number) => void;
  search: string;
  onSearchChange: (search: string) => void;
  publishedCourse?: any;
  tab: string;
  onTabChange: (tab: string) => void;
  enrolledTotal: number;
  onRemove: (selectedLearners: string[]) => void;
  isLoading: boolean;
  isEnrolling: boolean;
  groups: LearnerGroup[];
  groupNames: Record<string, string[]>;
  onEnrollGroup: (groupIds: string[], dueDate?: Date) => void;
}

const CourseLearnerUI = React.memo(
  ({
    onSave,
    learnerData,
    pages,
    currentPage,
    onPageChange,
    search,
    onSearchChange,
    publishedCourse,
    tab,
    onTabChange,
    enrolledTotal,
    onRemove,
    isLoading,
    isEnrolling,
    groups,
    groupNames,
    onEnrollGroup,
  }: Props) => {
    const [checkedLearnerIds, setCheckedLearnerIds] = React.useState<string[]>(
      [],
    );
    const [audience, setAudience] = React.useState<Audience>('learners');
    const [checkedGroups, setCheckedGroups] = React.useState<string[]>([]);
    const [dueDate, setDueDate] = React.useState<Date | null>(null);
    const [showDueDateModal, setShowDueDateModal] = React.useState(false);

    const isEnrolledTab = tab === ENROLLED_LEARNERS_TAB;
    const isGroupsAudience = audience === 'groups';

    const filteredGroups = groups.filter(group =>
      group.name.toLowerCase().includes(search.toLowerCase()),
    );

    const switchTab = (nextTab: string) => {
      setCheckedLearnerIds([]);
      setCheckedGroups([]);
      setAudience('learners');
      onTabChange(nextTab);
    };

    const onToggleGroup = (groupId: string) => {
      setCheckedGroups(current =>
        current.includes(groupId)
          ? current.filter(id => id !== groupId)
          : [...current, groupId],
      );
    };

    const onPrimaryAction = () => {
      if (isEnrolledTab) {
        onRemove([...checkedLearnerIds]);
        setCheckedLearnerIds([]);
      } else {
        setShowDueDateModal(true);
      }
    };

    const onToggle = (learnerId: string) => {
      setCheckedLearnerIds(current =>
        current.includes(learnerId)
          ? current.filter(id => id !== learnerId)
          : [...current, learnerId],
      );
    };

    const onToggleAll = () => {
      const allIds = learnerData.map(learner => learner.learnerId);
      const allChecked =
        allIds.length > 0 && allIds.every(id => checkedLearnerIds.includes(id));
      setCheckedLearnerIds(current =>
        allChecked
          ? current.filter(id => !allIds.includes(id))
          : Array.from(new Set([...current, ...allIds])),
      );
    };

    const enroll = (useDueDate: boolean) => {
      setShowDueDateModal(false);
      const selectedDueDate =
        useDueDate && dueDate instanceof Date ? dueDate : undefined;
      if (isGroupsAudience) {
        onEnrollGroup([...checkedGroups], selectedDueDate);
        setCheckedGroups([]);
        setCheckedLearnerIds([]);
      } else {
        onSave([...checkedLearnerIds], selectedDueDate);
        setCheckedLearnerIds([]);
      }
      setDueDate(null);
    };

    const renderTable = () => {
      if (isGroupsAudience) {
        return (
          <GroupsPicker
            groups={filteredGroups}
            checked={checkedGroups}
            search={search}
            onToggle={onToggleGroup}
          />
        );
      }
      if (isLoading && !learnerData.length) {
        return (
          <div className="LearnersLoading">
            <LoadingSpinner size={48} />
          </div>
        );
      }
      if (!learnerData.length) {
        if (isEnrolledTab && !search) {
          return (
            <LearnersEmpty
              title={courseLearnerText.emptyEnrolledTitle}
              text={courseLearnerText.emptyEnrolledText}
              actionTitle={courseLearnerText.addLearnersTab}
              onAction={() => switchTab(ADD_LEARNERS_TAB)}
            />
          );
        }
        return <LearnersEmpty title={courseLearnerText.cantFindLearners} />;
      }
      return (
        <LearnersTable
          learners={learnerData}
          checked={checkedLearnerIds}
          page={currentPage}
          pages={pages}
          onToggle={onToggle}
          onToggleAll={onToggleAll}
          onPageChange={onPageChange}
          groupNames={isEnrolledTab ? groupNames : undefined}
        />
      );
    };

    return (
      <div className="CourseLearnerContainer">
        {publishedCourse ? (
          <div className="EnrollLearnersWizardContainer">
            <div className="LearnerTabsRow">
              <div className="LearnerTabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={isEnrolledTab}
                  className={`LearnerTab ${
                    isEnrolledTab ? 'LearnerTabActive' : ''
                  }`}
                  onClick={() => switchTab(ENROLLED_LEARNERS_TAB)}>
                  {courseLearnerText.enrolledLearnersTab}
                  <span className="LearnerTabBadge">{enrolledTotal}</span>
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={!isEnrolledTab}
                  className={`LearnerTab ${
                    !isEnrolledTab ? 'LearnerTabActive' : ''
                  }`}
                  onClick={() => switchTab(ADD_LEARNERS_TAB)}>
                  {courseLearnerText.addLearnersTab}
                </button>
              </div>
              <div className="LearnerTabsSearch">
                {!isEnrolledTab && (
                  <AudienceToggle audience={audience} onChange={setAudience} />
                )}
                <LearnersSearch value={search} onChange={onSearchChange} />
              </div>
            </div>
            {renderTable()}
            {showDueDateModal && (
              <ModalDialog onClose={() => setShowDueDateModal(false)}>
                <div className="DueDateModalContent">
                  <h3>{courseLearnerText.dueDateModalTitle}</h3>
                  <TextInput2
                    uiType="minimal"
                    label={courseLearnerText.dueDate}
                    placeholder={courseLearnerText.dueDatePlaceholder}
                    value={dueDate ? dueDate.toDateString() : ''}
                    disabled={true}
                    onChange={() => {
                      // do nothing
                    }}
                  />
                  <DatePickerComponent
                    dueDate={dueDate}
                    setDueDate={setDueDate}
                    className="SearchInput"
                  />
                  <div className="DueDateModalActions">
                    <Button
                      title={courseLearnerText.skipAndEnroll}
                      uiType="text"
                      onClick={() => enroll(false)}
                    />
                    <Button
                      disabled={!dueDate}
                      title={courseLearnerText.saveAndEnroll}
                      onClick={() => enroll(true)}
                    />
                  </div>
                </div>
              </ModalDialog>
            )}
            <Button
              title={`${
                isEnrolledTab
                  ? courseLearnerText.removeSelected
                  : courseLearnerText.enroll
              }${
                (isGroupsAudience ? checkedGroups : checkedLearnerIds).length
                  ? ` (${
                      (isGroupsAudience ? checkedGroups : checkedLearnerIds)
                        .length
                    })`
                  : ''
              }`}
              onClick={onPrimaryAction}
              uiType={isEnrolledTab ? 'action_danger' : 'action'}
              icon={isEnrolledTab ? 'icon-trash' : undefined}
              disabled={
                isGroupsAudience
                  ? !checkedGroups.length
                  : !checkedLearnerIds.length
              }
              isLoading={isEnrolling}
              className={
                isEnrolledTab ? 'SaveButton RemoveButton' : 'SaveButton'
              }
            />
          </div>
        ) : (
          <CourseNotPublished subtitle={courseLearnerText.courseNotPublished} />
        )}
      </div>
    );
  },
);

export default CourseLearnerUI;
